import { useMemo, useState } from 'react';
import classNames from 'classnames';
import { DefaultTable } from '@hud/web-components/Table/Table';
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable,
  SortingState,
  RowSelectionState,
  FilterFn,
  Row,
} from '@tanstack/react-table';
import {
  Channel,
  isMissingChannel,
  SlackChannel,
  isSlackChannel,
} from '@hud/portal-dtos/channels.dtos';
import { HudIcon } from '@hud/web-components/Icons/HudIcons';
import IconButton from '@hud/web-components/DesignSystem/Components/IconButton/IconButton';
import Button from '@hud/web-components/DesignSystem/Components/Button/Button';
import Tooltip from '@hud/web-components/Tooltip/Tooltip';
import useSlackChannels from '../../../hooks/useSlackChannels';
import { TableContainer as StyledTableContainer } from './ChannelsTable.styles';
import TableContainer from '@hud/web-components/Container/TableContainer';
import { Search } from '@hud/web-components/Search/Search';
import Checkbox from '@hud/web-components/DesignSystem/Components/Checkbox/Checkbox';

/**
 * Format Slack error codes into user-friendly messages
 */
function formatSlackErrorMessage(errorCode: string, channelName?: string): string {
  switch (errorCode) {
    case 'channel_not_found':
      return channelName ? `Couldn't find channel #${channelName}` : 'Channel not found';
    case 'not_in_channel':
      return channelName
        ? `Hud app is not in channel #${channelName}`
        : 'Hud app is not in the channel';
    case 'is_archived':
      return channelName ? `Channel #${channelName} is archived` : 'Channel is archived';
    case 'invalid_auth':
      return 'Invalid Slack authentication - please reconnect your workspace';
    case 'token_revoked':
      return 'Slack token has been revoked - please reconnect your workspace';
    case 'missing_scope':
      return 'Missing permissions - please reinstall the Hud app';
    case 'account_inactive':
      return 'Slack account is inactive';
    case 'app_not_installed':
      return 'Hud app is not installed in this workspace';
    default:
      return `Error: ${errorCode}`;
  }
}

const columnHelper = createColumnHelper<Channel>();

const SLACK_APP_NOT_IN_CHANNEL_TOOLTIP =
  "The Hud Slack App isn't in this channel. Add it to ensure Alerts are delivered.";

export type EditChannelClickedEventArgs = {
  channel: Channel;
};

export type ConnectChannelsClickedEventArgs = {
  selectedChannels: Channel[];
};

interface ChannelsTableProps {
  channels: Channel[];
  isLoading: boolean;
  onAddChannelClicked: (channel: Channel) => void;
  onEditChannelClicked: (args: EditChannelClickedEventArgs) => void;
  onConnectChannelsClicked?: (args: ConnectChannelsClickedEventArgs) => void;
}

export default function ChannelsTable({
  channels,
  isLoading,
  onAddChannelClicked,
  onEditChannelClicked,
  onConnectChannelsClicked,
}: ChannelsTableProps) {
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'serviceName', desc: false },
  ]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const { data: slackChannels } = useSlackChannels();

  const selectedChannels = Object.keys(rowSelection)
    .filter((key) => rowSelection[key])
    .map((key) => channels[parseInt(key)])
    .filter(Boolean);

  const handleConnectChannels = () => {
    if (onConnectChannelsClicked && selectedChannels.length > 0) {
      onConnectChannelsClicked({ selectedChannels });
    }
  };

  // Custom global filter function to handle channel name filtering
  const customGlobalFilterFunction: FilterFn<Channel> = (
    row: Row<Channel>,
    _columnId: string,
    searchTerms: string,
  ) => {
    const channel = row.original;
    const searchValue = searchTerms.toLowerCase();

    // Check service name
    if (channel.serviceName && channel.serviceName.toLowerCase().includes(searchValue)) {
      return true;
    }

    // Check channel name for Slack channels
    if (isSlackChannel(channel)) {
      const channelName = channel.config.channelName.toLowerCase();
      if (channelName.includes(searchValue)) {
        return true;
      }
    }

    // For missing channels, check if "connect" matches
    if (isMissingChannel(channel) && 'connect'.includes(searchValue)) {
      return true;
    }

    return false;
  };

  const columns = useMemo(
    () => [
      // Selection column
      columnHelper.display({
        id: 'select',
        size: 25,
        maxSize: 25,
        minSize: 25,
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
      }),
      columnHelper.accessor('serviceName', {
        header: 'Service',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('id', {
        id: 'channel',
        header: 'Channel',
        cell: (info) => {
          const channel = info.row.original;
          if (isMissingChannel(channel)) {
            return (
              <div
                className="channel missing"
                role="button"
                aria-label="add channel"
                onClick={() => onAddChannelClicked(channel)}
              >
                <HudIcon icon="designSystem.plug" />
                <span>Connect a channel</span>
              </div>
            );
          }
          const { channelId, channelName } = (channel as SlackChannel).config;
          const slackChannel = slackChannels?.channels.find((c) => c.id === channelId);
          const isMemberOfSlackChannel = slackChannel?.isMember;

          // Determine the warning/error message to display
          let warningMessage = '';
          if (channel.errorMessage) {
            // Show formatted error message if there's an error
            warningMessage = formatSlackErrorMessage(channel.errorMessage, channelName);
          } else if (!isMemberOfSlackChannel) {
            // Show app not in channel warning if no error but app isn't in channel
            warningMessage = SLACK_APP_NOT_IN_CHANNEL_TOOLTIP;
          }

          return (
            <div
              className={classNames('channel', {
                warning: !!warningMessage,
                connected: !warningMessage,
              })}
              aria-label={`channel ${channelName} - ${warningMessage ? `warning: ${warningMessage}` : ''}`}
            >
              <HudIcon icon="designSystem.hashtag" />
              <span aria-label={`name`}>{channelName}</span>
              {warningMessage && (
                <Tooltip description={warningMessage} openDelay={0}>
                  <HudIcon icon="designSystem.infoCircle" />
                </Tooltip>
              )}
            </div>
          );
        },
      }),

      columnHelper.display({
        id: 'edit',
        size: 20,
        maxSize: 20,
        minSize: 20,
        enableResizing: false,
        meta: {
          narrow: true,
        },
        cell: (info) => {
          const channel = info.row.original;
          if (isMissingChannel(channel)) {
            return null;
          }
          return (
            <IconButton
              icon="designSystem.pencil"
              aria-label="edit"
              onClick={() => onEditChannelClicked({ channel })}
            />
          );
        },
      }),
    ],
    [onAddChannelClicked, onEditChannelClicked, slackChannels],
  );

  const table = useReactTable({
    data: channels,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    columnResizeMode: 'onChange',
    enableRowSelection: true,
    enableColumnResizing: false,
    globalFilterFn: customGlobalFilterFunction,

    state: {
      sorting,
      globalFilter,
      rowSelection,
    },
  });

  return (
    <StyledTableContainer>
      <TableContainer.Header>
        <TableContainer.Filters>
          <Search inline value={globalFilter} onSearchChange={setGlobalFilter} />
        </TableContainer.Filters>
        {selectedChannels.length > 0 && (
          <div>
            <Button onClick={handleConnectChannels} size="small">
              <HudIcon icon="designSystem.plug" size="20px" />
              <span>
                Connect {selectedChannels.length} channel
                {selectedChannels.length !== 1 ? 's' : ''}
              </span>
            </Button>
          </div>
        )}
      </TableContainer.Header>
      <DefaultTable
        table={table}
        loading={isLoading}
        aria-busy={isLoading}
        options={{
          emptyStateProps: {
            title: 'No channels configured',
          },
        }}
      />
    </StyledTableContainer>
  );
}
